import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { allBreedsQuery } from '@/appview/RegisterChipV2/PetInfoStepV2/BreedSelect.graphql';
import { Species } from '@/types/gql-op-types';
import BreedSelectModal from '@/appview/RegisterChipV2/PetInfoStepV2/BreedSelectModal';
import { PortalInput, PortalTextarea } from '../../components/FormField/PortalInput';
import { PortalSelect } from '../../components/FormField/PortalSelect';
import { PortalField } from '../../components/FormField/PortalField';
import { ToggleGroup } from '../../components/FormField/ToggleGroup';
import { CheckboxInline } from '../../components/FormField/CheckboxInline';
import { FormGrid } from '../../components/FormField/FormGrid';

export interface PetFormData {
  petName: string;
  species: string;
  breed: string;
  mixedBreed: boolean;
  gender: string;
  dobYear: string;
  dobMonth: string;
  dobDay: string;
  spayedNeutered: string;
  color: string;
  weight: string;
  additionalInfo: string;
}

export const INITIAL_PET: PetFormData = {
  petName: '',
  species: 'dog',
  breed: '',
  mixedBreed: false,
  gender: '',
  dobYear: '',
  dobMonth: '',
  dobDay: '',
  spayedNeutered: '',
  color: '',
  weight: '',
  additionalInfo: '',
};

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [
  { value: '', label: 'Year' },
  ...Array.from({ length: CURRENT_YEAR - 2001 + 1 }, (_, i) => {
    const y = String(CURRENT_YEAR - i);
    return { value: y, label: y };
  }),
];

export const MONTHS = [
  { value: '', label: 'Month' },
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

const DAYS = [
  { value: '', label: 'Day' },
  ...Array.from({ length: 31 }, (_, i) => {
    const d = String(i + 1).padStart(2, '0');
    return { value: d, label: String(i + 1) };
  }),
];

export function speciesFormToGql(species: string): Species | undefined {
  if (species === 'dog') return Species.DOG;
  if (species === 'cat') return Species.CAT;
  if (species === 'other') return Species.OTHER;
  return undefined;
}

export function validatePetForm(pet: PetFormData, { requireSpayedNeutered = true } = {}): Record<string, boolean> {
  const errs: Record<string, boolean> = {};
  if (!pet.petName.trim()) errs.petName = true;
  if (!pet.gender) errs.gender = true;
  if (!pet.dobYear.trim()) errs.dobYear = true;
  if (requireSpayedNeutered && !pet.spayedNeutered) errs.spayedNeutered = true;
  if (!pet.color.trim()) errs.color = true;
  return errs;
}

interface PetFormFieldsProps {
  pet: PetFormData;
  errors: Record<string, boolean>;
  onChange: <K extends keyof PetFormData>(key: K, value: PetFormData[K]) => void;
  hideSpayedNeutered?: boolean;
}

export default function PetFormFields({ pet, errors, onChange, hideSpayedNeutered }: PetFormFieldsProps) {
  const gqlSpecies = speciesFormToGql(pet.species);
  const { data: breedsData } = useQuery(allBreedsQuery, {
    variables: { species: gqlSpecies },
    skip: !gqlSpecies,
  });
  const breedOptions = useMemo(
    () => (breedsData?.allBreeds ?? []).map((b: { id: string; name: string }) => ({ value: b.id, label: b.name })),
    [breedsData],
  );

  return (
    <FormGrid>
      <PortalField label="Pet Name *" error="Required" showError={errors.petName}>
        <PortalInput
          value={pet.petName}
          onChange={(e) => onChange('petName', e.target.value)}
          placeholder="e.g. Biscuit"
          hasError={errors.petName}
        />
      </PortalField>

      <PortalField label="Species">
        <ToggleGroup
          options={[
            { value: 'dog', label: 'Dog' },
            { value: 'cat', label: 'Cat' },
            { value: 'other', label: 'Other' },
          ]}
          value={pet.species}
          onChange={(v) => {
            onChange('species', v);
            onChange('breed', '');
          }}
        />
      </PortalField>

      <PortalField label="Breed">
        <BreedSelectModal
          options={breedOptions}
          value={pet.breed}
          setValue={(v) => onChange('breed', v)}
          placeholder="e.g. Labrador Retriever"
          variant="portal"
        />
        <CheckboxInline
          id="mixed-breed"
          label="Mixed breed"
          checked={pet.mixedBreed}
          onChange={(v) => onChange('mixedBreed', v)}
        />
      </PortalField>

      <PortalField label="Gender *" error="Required" showError={errors.gender}>
        <ToggleGroup
          options={[
            { value: 'male', label: 'Male' },
            { value: 'female', label: 'Female' },
          ]}
          value={pet.gender}
          onChange={(v) => onChange('gender', v)}
        />
      </PortalField>

      <PortalField
        label="Date of Birth *"
        hint="Year is required. Month and day are optional."
        error="Year is required"
        showError={errors.dobYear}
        fullWidth
      >
        <div style={{ display: 'flex', gap: 8 }}>
          <PortalSelect
            value={pet.dobYear}
            onChange={(e) => onChange('dobYear', e.target.value)}
            style={{ flex: 1 }}
            hasError={errors.dobYear}
          >
            {YEARS.map((y) => (
              <option key={y.value} value={y.value}>
                {y.label}
              </option>
            ))}
          </PortalSelect>
          <PortalSelect value={pet.dobMonth} onChange={(e) => onChange('dobMonth', e.target.value)} style={{ flex: 1 }}>
            {MONTHS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </PortalSelect>
          <PortalSelect value={pet.dobDay} onChange={(e) => onChange('dobDay', e.target.value)} style={{ flex: 1 }}>
            {DAYS.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </PortalSelect>
        </div>
      </PortalField>

      {!hideSpayedNeutered && (
        <PortalField label="Spayed / Neutered *" error="Required" showError={errors.spayedNeutered}>
          <ToggleGroup
            options={[
              { value: 'yes', label: 'Yes' },
              { value: 'no', label: 'No' },
            ]}
            value={pet.spayedNeutered}
            onChange={(v) => onChange('spayedNeutered', v)}
          />
        </PortalField>
      )}

      <PortalField label="Color / Markings *" error="Required" showError={errors.color}>
        <PortalInput
          value={pet.color}
          onChange={(e) => onChange('color', e.target.value)}
          placeholder="e.g. Brown"
          hasError={errors.color}
        />
      </PortalField>

      <PortalField label="Weight (lbs)">
        <PortalInput
          value={pet.weight}
          onChange={(e) => onChange('weight', e.target.value)}
          placeholder="e.g. 55"
          type="number"
        />
      </PortalField>

      <PortalField label="Additional Info" fullWidth>
        <PortalTextarea
          value={pet.additionalInfo}
          onChange={(e) => onChange('additionalInfo', e.target.value)}
          placeholder="Any additional notes..."
          rows={3}
        />
      </PortalField>
    </FormGrid>
  );
}
